import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Flex, Input, Skeleton, Tag, Tooltip, Typography } from "antd"
import { EnvironmentOutlined, SearchOutlined } from "@ant-design/icons"
import { useTheme } from "context/AntdThemeContext"
import { colors } from "themes/variables"
import { getGuideTrips, updateTripColors } from "store/trips"
import { metadataSelector } from "store/selectors"
import {
  getEventPreviewColors,
  getPrimaryCalendarTextColor,
  isSameHexColor,
  normalizeCssColorToHex
} from "pagesV2/pro/Calendar/calendarColorUtils"
import { SettingsTitle } from "../ProSettings/SettingsTitle"
import { toaster } from "components/common/Toast"
import CustomPagination from "components/common/CustomPagination"
import styles from "./index.module.css"

const { Text } = Typography

const isValidHex = (hex) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)

const getDefaultCalendarColor = () => {
  if (typeof document === "undefined" || !document.body) return colors.blueSurface
  const resolvedColor = getComputedStyle(document.body).getPropertyValue("--color-primary-surface").trim()
  return normalizeCssColorToHex(resolvedColor)
}

const TilePreview = ({ backgroundColor, borderColor, previewTime, textColor, title }) => {
  return (
    <Tag bordered={false} className={styles.tilePreview} style={{ backgroundColor, borderColor, color: textColor }}>
      <span className={styles.tileTime} style={{ color: textColor }}>{previewTime}</span>
      <span className={styles.tileTitle} style={{ color: textColor }}>{title}</span>
    </Tag>
  )
}

TilePreview.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  previewTime: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
}

const PAGE_SIZE = 10

export const CalendarExperienceColors = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { theme } = useTheme()

  const trips = useSelector((state) => state.trips?.items || [])
  const loading = useSelector((state) => state.trips?.loading)
  const metadata = useSelector(metadataSelector("trips"))

  const [defaultColor, setDefaultColor] = useState(getDefaultCalendarColor)
  const [primaryTextColor, setPrimaryTextColor] = useState(() => getPrimaryCalendarTextColor(theme))
  const [colorMap, setColorMap] = useState({})
  const [hexDraft, setHexDraft] = useState({})
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  useEffect(() => {
    setDefaultColor(getDefaultCalendarColor())
    setPrimaryTextColor(getPrimaryCalendarTextColor(theme))
  }, [theme])

  useEffect(() => {
    dispatch(getGuideTrips(null, { q: { status_eq: "published", title_cont: search || undefined }, page, per_page: pageSize, sort_by: "updated_at_desc" }))
  }, [dispatch, page, pageSize, search])

  useEffect(() => {
    if (trips.length === 0) return
    setColorMap((prev) => {
      const fromServer = trips.reduce((acc, trip) => {
        if (trip.color) acc[trip.uuid] = trip.color
        return acc
      }, {})
      return { ...fromServer, ...prev }
    })
  }, [trips])

  const handleColorChange = (id, color) => {
    setColorMap((prev) => {
      if (isSameHexColor(color, defaultColor)) {
        const next = { ...prev }
        delete next[id]
        return next
      }

      return { ...prev, [id]: color }
    })
    setHexDraft((prev) => ({ ...prev, [id]: color }))
    setDirty(true)
  }

  const handleHexInput = (id, value) => {
    setHexDraft((prev) => ({ ...prev, [id]: value }))
    if (isValidHex(value)) {
      setColorMap((prev) => {
        if (isSameHexColor(value, defaultColor)) {
          const next = { ...prev }
          delete next[id]
          return next
        }

        return { ...prev, [id]: value }
      })
      setDirty(true)
    }
  }

  const handleReset = async (id) => {
    // Remove from local state immediately for responsive UI
    setColorMap((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setHexDraft((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    
    // Send reset to API by including the trip with default color (null/undefined)
    const tripColors = Object.entries(colorMap)
      .filter(([uuid]) => uuid !== id) // Exclude the trip being reset from current colors
      .map(([uuid, color]) => ({ uuid, color }))
    
    // Add the trip being reset with default color
    tripColors.push({ uuid: id, color: defaultColor })
    
    try {
      await dispatch(updateTripColors(tripColors))
      toaster.success(t("calendar.experience_colors.save_success"))
    } catch {
      console.error("Failed to reset color")
      // Revert local state on error
      setColorMap((prev) => ({ ...prev, [id]: defaultColor }))
      setHexDraft((prev) => ({ ...prev, [id]: defaultColor }))
    }
  }

  const handleSave = async () => {
    const tripColors = Object.entries(colorMap).map(([id, color]) => ({ uuid: id, color }))
    setSaving(true)
    try {
      await dispatch(updateTripColors(tripColors))
      setDirty(false)
      toaster.success(t("calendar.experience_colors.save_success"))
    } catch {
      console.error("Failed to save colors")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const saved = trips.reduce((acc, trip) => {
      if (trip.color) acc[trip.uuid] = trip.color
      return acc
    }, {})
    setColorMap(saved)
    setHexDraft({})
    setDirty(false)
  }

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handlePageChange = (newPage) => setPage(newPage)

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize)
    setPage(1)
  }

  return (
    <Flex vertical>
      <SettingsTitle subTitle={t("calendar.experience_colors.subtitle")} />

      <Input
        prefix={<SearchOutlined />}
        placeholder={t("calendar.experience_colors.search_placeholder")}
        value={search}
        onChange={handleSearch}
        allowClear
      />

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} className="mt-s" />
      ) : (
        <Flex vertical gap="small" flex={1} className="overflow-auto my-s">
          {trips.length === 0 && (
            <Text type="secondary">{t("calendar.experience_colors.empty_state")}</Text>
          )}
          {trips.map((trip) => {
            const current = colorMap[trip.uuid] || defaultColor
            const isDefaultColor = isSameHexColor(current, defaultColor)
            const previewColors = getEventPreviewColors(current, defaultColor, primaryTextColor)
            return (
              <Flex key={trip.id} align="center" gap="large" wrap className={styles.row}>
                <Flex vertical gap={4} className={styles.info}>
                  <Text className={styles.name}>{trip.title}</Text>
                  {trip.address && (
                    <Flex align="center" gap={4}>
                      <EnvironmentOutlined className={styles.locationIcon} />
                      <Text className={styles.location}>{trip.address}</Text>
                    </Flex>
                  )}
                </Flex>
                <TilePreview
                  backgroundColor={previewColors.backgroundColor}
                  borderColor={previewColors.borderColor}
                  previewTime={t("calendar.experience_colors.preview_time")}
                  textColor={previewColors.textColor}
                  title={trip.title}
                />
                <Flex align="center" gap="small">
                  <input
                    type="color"
                    value={current}
                    onChange={(e) => handleColorChange(trip.uuid, e.target.value)}
                    className={styles.colorInput}
                    style={{ borderColor: current }}
                  />
                  <Input
                    value={hexDraft[trip.uuid] ?? current}
                    onChange={(e) => handleHexInput(trip.uuid, e.target.value)}
                    className={styles.hexInput}
                    maxLength={7}
                    status={hexDraft[trip.uuid] && !isValidHex(hexDraft[trip.uuid]) ? "error" : ""}
                  />
                  <Tooltip title={isDefaultColor ? t("calendar.experience_colors.tooltip_default") : t("calendar.experience_colors.tooltip_reset")}>
                    <Button size="small" type="link" onClick={() => handleReset(trip.uuid)} disabled={isDefaultColor}>
                      {t("calendar.experience_colors.reset")}
                    </Button>
                  </Tooltip>
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      )}

      {metadata?.total_results > pageSize && (
        <CustomPagination
          metadata={metadata}
          pageSize={pageSize}
          total={metadata.total_results}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <Flex gap="small" className="pb-4">
        <Button onClick={handleCancel} color="primary" variant="outlined" disabled={!dirty}>
          {t("global.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="solid" disabled={!dirty} loading={saving}>
          {t("calendar.experience_colors.save_changes")}
        </Button>
      </Flex>
    </Flex>
  )
}
